import { CommandProps, Extension } from '@tiptap/core';
import { hasNestedListContent, LIST_CONTAINER_TYPES, isNestedList, findListContext } from '../list/tiptapListUtils';
import { DEFAULT_TIPTAP_EXTENSION_PRIORITY, TiptapNodeType } from '../../tiptapTypes';
import { Node } from '@tiptap/pm/model';
import { EditorState, TextSelection, Transaction } from '@tiptap/pm/state';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        listDeindentHandler: {
            /**
             * Lift a list item that contains nested list content
             */
            liftListItemWithNestedContent: () => ReturnType;
        };
    }
}

type ExtractedContent = {
    nestedListNode: Node;
    paragraphNodes: Node[];
};

type ListSegments = {
    itemsBefore: Node[];
    itemsAfter: Node[];
    currentItemIndex: number;
};

/**
 * Extract nested list content and paragraphs from a list item
 */
const extractListItemContent = (listItem: Node): ExtractedContent | null => {
    let nestedListNode: Node | null = null;
    const paragraphNodes: Node[] = [];

    listItem.content.forEach((child) => {
        if (LIST_CONTAINER_TYPES.has(child.type.name as TiptapNodeType)) {
            nestedListNode = child;
        } else if (child.type.name === TiptapNodeType.paragraph && child.content.size > 0) {
            paragraphNodes.push(child);
        }
    });

    if (!nestedListNode || paragraphNodes.length === 0) return null;

    return { nestedListNode, paragraphNodes };
};

/**
 * Find the current item index and split the parent list into before/after segments
 */
const segmentParentList = (state: EditorState, parentList: Node, listItemPos: number): ListSegments | null => {
    // Use resolved position to get the index directly
    const $listItemPos = state.doc.resolve(listItemPos);
    const currentItemIndex = $listItemPos.index();

    const itemsBefore: Node[] = [];
    const itemsAfter: Node[] = [];

    for (let i = 0; i < parentList.childCount; i++) {
        if (i < currentItemIndex) {
            itemsBefore.push(parentList.child(i));
        } else if (i > currentItemIndex) {
            itemsAfter.push(parentList.child(i));
        }
    }

    return { itemsBefore, itemsAfter, currentItemIndex };
};

/**
 * Build the replacement node structure for the deindented content
 */
const buildReplacementNodes = (
    state: EditorState,
    parentList: Node,
    segments: ListSegments,
    content: ExtractedContent,
): Node[] => {
    const { itemsBefore, itemsAfter } = segments;
    const { nestedListNode, paragraphNodes } = content;
    const replacementNodes: Node[] = [];

    // Add items before current item as a separate list
    if (itemsBefore.length > 0) {
        replacementNodes.push(parentList.type.create(parentList.attrs, itemsBefore));
    }

    // Add deindented paragraphs
    paragraphNodes.forEach((paragraphNode) => {
        if (paragraphNode.content.size > 0) {
            replacementNodes.push(state.schema.nodes.paragraph.create(paragraphNode.attrs, paragraphNode.content));
        }
    });

    // Combine nested items with remaining items
    const remainingListItems: Node[] = [];
    nestedListNode.content.forEach((item: Node) => remainingListItems.push(item));
    itemsAfter.forEach((item) => remainingListItems.push(item));

    if (remainingListItems.length > 0) {
        const startNumber = nestedListNode.attrs.start || 1;
        const listAttrs = { ...parentList.attrs, start: startNumber };
        replacementNodes.push(parentList.type.create(listAttrs, remainingListItems));
    }

    return replacementNodes;
};

/**
 * Position cursor at the deindented paragraph
 */
const setCursorPosition = (transaction: Transaction, listStartPos: number, itemsBefore: Node[], parentList: Node) => {
    let targetPos = listStartPos;
    if (itemsBefore.length > 0) {
        targetPos += parentList.type.create(parentList.attrs, itemsBefore).nodeSize;
    }
    const resolvedPos = transaction.doc.resolve(targetPos + 1);
    if (resolvedPos.parent.type.name === TiptapNodeType.paragraph) {
        transaction.setSelection(TextSelection.create(transaction.doc, targetPos + 1));
    }
};

/**
 * Handle lifting a list item with nested content using ProseMirror transactions
 *
 * This function handles the complex case of correctly deindenting a list item numbering that contains nested lists.
 * It only operates on top-level lists to avoid issues with deeply nested list items.
 *
 * Example transformation:
 * Before:
 *   1. one
 *   2. two
 *      a. child 1
 *      b. child 2
 *   3. three
 *   4. four
 *
 * After deindenting item 2:
 *   1. one
 *
 *   two
 *
 *   1. child 1
 *   2. child 2
 *   3. three
 *   4. four
 *
 * Logic:
 * 1. Find the list item and its parent list
 * 2. Check if the parent list is at the top level (not nested within another list item)
 * 3. If nested, return false to let default handling take over
 * 4. Extract nested list content and paragraph content from the item
 * 5. Split the parent list into: items before, items after
 * 6. Create replacement structure:
 *    - List of items before (if any)
 *    - Deindented paragraph(s)
 *    - New list combining promoted nested items + remaining items
 * 7. Replace entire parent list with new structure in single transaction
 * 8. Position cursor at the deindented paragraph
 */
export const handleLiftListItemWithNestedContent = (commandProps: CommandProps): boolean => {
    const { state, dispatch } = commandProps;
    if (!dispatch) return false;

    //  Find list context
    const context = findListContext(state, state.selection);
    if (!context) return false;

    const { listItem, parentList, parentListPos } = context;

    // Check if item has nested content
    if (!hasNestedListContent(listItem)) return false;

    // Only perform this operation on top-level lists
    // If the list is nested within another list item, let default handling take over
    if (isNestedList(state, parentList)) return false;

    try {
        const content = extractListItemContent(listItem);
        if (!content) return false;

        const segments = segmentParentList(state, parentList, context.listItemPos);
        if (!segments) return false;

        // Build and apply transformation
        const transaction = state.tr;
        const replacementNodes = buildReplacementNodes(state, parentList, segments, content);
        const listStartPos = parentListPos;
        const listEndPos = parentListPos + parentList.nodeSize;

        if (replacementNodes.length > 0) {
            transaction.replaceWith(listStartPos, listEndPos, replacementNodes);
            setCursorPosition(transaction, listStartPos, segments.itemsBefore, parentList);
        } else {
            transaction.delete(listStartPos, listEndPos);
        }

        dispatch(transaction);
        return true;
    } catch (error) {
        console.error('Error lifting list item with nested content:', error);
        return false;
    }
};

export const ListDeindentHandler = Extension.create({
    name: 'listDeindentHandler',
    // Increase the priority so that this handler runs before the default list handling
    priority: DEFAULT_TIPTAP_EXTENSION_PRIORITY + 1,

    addCommands() {
        return {
            liftListItemWithNestedContent: () => handleLiftListItemWithNestedContent,
        };
    },

    addKeyboardShortcuts() {
        return {
            ['Shift-Tab']: ({ editor }) => editor.commands?.liftListItemWithNestedContent(),
        };
    },
});
